import { useRef, useEffect, useState } from 'react';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineContentDescription,
  TimelineContentLabel,
  TimelineItem,
  TimelineItemLabel,
} from '../ui/comments-timeline';
import { ICommentWithUser } from '@/redux/commet/comment';
import { deltaToHtml } from '@/lib/quill';
import { formatTimestamp, getUserFullName } from '@/lib/utils';
import DeletePostComment from '@/redux/commet/DeletePostComment';

export default function CommentsTimeline({
  comments,
}: {
  comments: ICommentWithUser[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(2);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollPosition = container.scrollTop;
      const itemHeight = container.scrollHeight / comments.length;
      const newActiveIndex = Math.max(
        1,
        Math.floor(scrollPosition / itemHeight) + 2
      );
      setActiveIndex(newActiveIndex);
    };

    container.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [comments]);

  const scrollToItem = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const itemHeight = container.scrollHeight / comments.length;
    container.scrollTo({
      top: Math.max(0, (index - 1) * itemHeight), // Update 3: Modified scrollToItem function
      behavior: 'smooth',
    });
  };

  return (
    <div className="w-full max-w-2xl mx-auto h-[600px]">
      <div
        ref={containerRef}
        className="h-full overflow-y-auto scrollbar-hide font-tertiary"
      >
        <Timeline>
          {comments?.map((comment, index) => (
            <TimelineItem key={comment?._id} active={index === activeIndex}>
              <TimelineItemLabel className="font-medium text-sm text-[#808080] hidden lg:block">
                {formatTimestamp(comment.commentedAt)}
              </TimelineItemLabel>
              <TimelineConnector
                active={index === activeIndex}
                onClick={() => scrollToItem(index)}
              />
              <TimelineContent>
                <TimelineContentLabel className="font-secondary text-lg font-normal text-[#434343]">
                  {getUserFullName(comment.user)}
                </TimelineContentLabel>
                <TimelineContentDescription className="flex gap-5">
                  <div
                    id="quill-editor"
                    className="font-tertiary text-sm text-[#808080] w-[90%] text-justify "
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: deltaToHtml(comment.comment),
                    }}
                  />
                  <DeletePostComment
                    commentId={comment._id}
                    postId={comment.post}
                    userId={comment.user?._id}
                  />
                </TimelineContentDescription>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </div>
    </div>
  );
}
